import RecaptchaUtil from "../utils/RecaptchaUtil";
import strings from "../strings";

class Auth {
	static async signUp(username, email, password) {
		const result = {};

		try {
			const recaptchaToken = await RecaptchaUtil.execute("signup");

			if (recaptchaToken) {
				const data = {
					"username": username,
					"email": email,
					"password": password,
				};
				const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/admin`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							"X-Recaptcha-Token": recaptchaToken
						},
						body: JSON.stringify(data)
					}
				);

				result.status = response.status;
			} else {
				result.status = 403;
			}
		} catch (error) {
			result.status = (error.response) ? error.response.status : 500;
		} finally {
			return result;
		}
	}

	static async login(email, password) {
		const result = {};

		try {
			const recaptchaToken = await RecaptchaUtil.execute("login");

			if (recaptchaToken) {
				const data = {email, password};
				const response = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/getToken`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							"X-Recaptcha-Token": recaptchaToken
						},
						body: JSON.stringify(data)
					}
				);

				switch (response.status) {
					case 201:
						const {token, exp, refresh_token, idAdmin, role} = await response.json();
						sessionStorage.setItem("session", JSON.stringify({token, exp, refresh_token, idAdmin, role}));
						break;
					case 403:
						const {message} = await response.json();
						if (message === "recaptcha-failed") {
							result.message = strings.recaptchaValidationFailed;
							result.severity = "danger";
						} else {
							result.message = strings.profileNotYetActivated;
							result.severity = "warning";
						}
						break;
					default:
				}

				result.status = response.status;
			} else {
				result.message = strings.recaptchaValidationFailed;
				result.severity = "danger";
				result.status = 403;
			}
		} catch (error) {
			result.status = (error.response) ? error.response.status : 500;
		} finally {
			return result;
		}
	}

	static async logout() { // async is necessary
		sessionStorage.removeItem("session");
	}

	static getSession() {
		return sessionStorage.getItem("session") ? JSON.parse(sessionStorage.getItem("session")) : null;
	}

	static isLoggedIn() {
		const session = Auth.getSession();

		if (session) {
			const now = new Date().getTime();
			if (((session.exp * 1000) - now) >= 0) {
				return true;
			}
		}

		return false;
	}

	static isTokenAboutToExpire() {
		const session = Auth.getSession();

		if (session) {
			const now = new Date().getTime();
			if (((session.exp * 1000) - now) < 600000) { // less than 10 min
				return true;
			}
		}

		return false;
	}

	static async refreshToken() {
		const result = {};
		const session = Auth.getSession();

		if (session) {
			try {
				const data = {refresh_token: session.refresh_token};
				const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/admin/refreshToken`,
					{
						method: "POST",
						headers: {"Content-Type": "application/json"},
						body: JSON.stringify(data)
					}
				);
	
				if (response.ok) {
					const {token, exp, refresh_token, idAdmin, role} = await response.json();
					sessionStorage.setItem("session", JSON.stringify({token, exp, refresh_token, idAdmin, role}));
				}

				result.status = response.status;
			} catch (error) {
				result.status = (error.response) ? error.response.status : 500;
			}
		} else {
			result.status = 401;
		}

		return result;
	}

	static async forgotPassword(email) {
		const result = {};
		const data = {email};

		try {
			const recaptchaToken = await RecaptchaUtil.execute("forgot_password");

			if (recaptchaToken) {
				const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/admin/forgotPassword`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							"X-Recaptcha-Token": recaptchaToken
						},
						body: JSON.stringify(data)
					}
				);

				result.status = response.status;
			} else {
				result.status = 403;
			}
		} catch (error) {
			result.status = (error.response) ? error.response.status : 500;
		} finally {
			return result;
		}
	}

	static async resetPassword(id, email, newPassword, token) {
		const result = {};
		const data = {id, email, newPassword, token};

		try {
			const recaptchaToken = await RecaptchaUtil.execute("reset_password");

			if (recaptchaToken) {
				const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/admin/resetPassword`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							"X-Recaptcha-Token": recaptchaToken
						},
						body: JSON.stringify(data)
					}
				);

				if (response.status === 403) {
					const {message} = await response.json();
					result.message = (message === "recaptcha-failed")
						? strings.recaptchaValidationFailed
						: strings.unauthorisedRequest;
				}

				result.status = response.status;
			} else {
				result.message = strings.recaptchaValidationFailed;
				result.status = 403;
			}
		} catch (error) {
			result.status = (error.response) ? error.response.status : 500;
		} finally {
			return result;
		}
	}
}

export default Auth;
